/**
 * The design system the widgets render with. The editor canvas and the
 * published page both read their components from the nearest
 * DesignSystemScope, so neither imports a design system: the app root scopes
 * the canvas to Duro, the page renderer scopes each page to the site's, and
 * the design-system sandbox scopes it to an uploaded one — without this module
 * pulling any design system into that sandbox's bundle.
 */
import { createContext, useContext, type ReactNode } from "react";
import type { DesignSystemModule } from "@website-builder/ds-contract";

const DesignSystemContext = createContext<DesignSystemModule | null>(null);

/** The active design system (the nearest DesignSystemScope's). */
export function useDesignSystem(): DesignSystemModule {
  const ds = useContext(DesignSystemContext);
  if (!ds)
    throw new Error(
      "No design system in scope: render widgets inside <DesignSystemScope>",
    );
  return ds;
}

/** Render `children` with `ds` — and inside its own Provider, if it has one. */
export function DesignSystemScope({
  ds,
  children,
}: {
  readonly ds: DesignSystemModule;
  readonly children?: ReactNode;
}) {
  const Provider = ds.Provider;
  return (
    <DesignSystemContext value={ds}>
      {Provider ? <Provider>{children}</Provider> : children}
    </DesignSystemContext>
  );
}
